import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import Stars from "./Stars";
import './Hero.css';
import Button from "./Button";
import Mascot from "./Mascot";
import ProductDivider from "./ProductDivider";
import ContactModal from "./ContactModal";

const Hero = () => {
  const location = useLocation();
  const isIndustrial = location.pathname.startsWith('/industrial');
  const isIndustrialHome = location.pathname === '/industrial/home';
  const isHousehold = location.pathname.startsWith('/household');
  const backgroundImage = isIndustrial ? '/images/Restaurant2.png' : '/images/Kitchen5.png';
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Add scroll behavior control
  useEffect(() => {
    const preventScrollUp = (e) => {
      if (window.scrollY === 0 && e.deltaY < 0) {
        e.preventDefault();
      }
    };

    // Add event listener for mouse wheel
    window.addEventListener('wheel', preventScrollUp, { passive: false });
    
    // Add event listener for touch devices
    let touchStart = 0;
    window.addEventListener('touchstart', (e) => {
      touchStart = e.touches[0].clientY;
    }, { passive: false });

    window.addEventListener('touchmove', (e) => {
      const touchEnd = e.touches[0].clientY;
      if (window.scrollY === 0 && touchEnd > touchStart) {
        e.preventDefault();
      }
    }, { passive: false });

    // Cleanup
    return () => {
      window.removeEventListener('wheel', preventScrollUp);
      window.removeEventListener('touchstart', (e) => {
        touchStart = e.touches[0].clientY;
      });
      window.removeEventListener('touchmove', (e) => {
        const touchEnd = e.touches[0].clientY;
        if (window.scrollY === 0 && touchEnd > touchStart) {
          e.preventDefault();
        }
      });
    };
  }, []);

  const scrollToProducts = () => {
    // Check if we're on the home page
    if (window.location.pathname === '/') {
      const productsSection = document.getElementById('our-products');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If we're not on the home page, navigate to the products page
      window.location.href = '/household/products';
    }
  };

  const handleButtonClick = () => {
    if (isIndustrialHome) {
      setIsContactModalOpen(true);
    } else {
      scrollToProducts();
    }
  };

  return (
    <motion.section
      id="hero"
      className="relative z-10 flex items-center min-h-screen"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0) 85%, rgba(255, 255, 255, 1)), url(${backgroundImage})`,
        marginTop: 0,
        paddingTop: 0,
        position: 'relative',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2, ease: "easeOut" }}
    >
      {/* Sparkly Stars in the background */}
      {/* <div className="absolute inset-0 pointer-events-none z-0">
        <Stars
          count={30}
          sizeRange={[5, 15]}
          durationRange={[3, 6]}
          opacityRange={[0.4, 0.9]}
        />
      </div> */}

      {/* Main wrapper */}
      <motion.div
        className="hero-content relative z-10 w-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 1 }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-6 h-full">
          <div className="hidden lg:block h-full">
            <div className="w-full h-full bg-gray-100 opacity-0 rounded-lg"></div>
          </div>

          {/* Right column */}
          <div className="flex flex-col justify-center items-center text-center space-y-4 md:space-y-6 w-full">
            <motion.h1
              className="font-extrabold leading-tight text-[#D3242A] mb-1 relative text-center w-full text-[4rem] sm:text-[2.5rem] md:text-[3rem] lg:text-[3.5rem]"
              style={{ fontFamily: "TwCenMTCondensedExtraBold, sans-serif", fontStyle: "normal" }}
            >
              {isIndustrial && (
                <div className="absolute inset-0 -z-10" style={{ transform: 'scale(1.2)' }}>
                  <div className="absolute inset-0" />
                </div>
              )}
              <div className="text-center w-full">
                <motion.span 
                  className="block mb-[-0.5rem]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  A PROVEN
                </motion.span>
                <motion.span 
                  className="block whitespace-nowrap"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                >
                  POWERFUL CLEAN
                </motion.span>
              </div>
            </motion.h1>

            {/* Subheading */}
            <motion.div
              className="inline-block bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-lg drop-shadow-md p-4 sm:p-6 md:p-8 text-center w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.1 }}
            >
              <motion.p 
                className="text-[#222222] leading-tight font-medium mb-0 text-center w-full text-[2.25rem] sm:text-[1.5rem] md:text-[1.875rem]" 
                style={{ fontFamily: "TwCenMTCondensedExtraBold, sans-serif", fontStyle: "normal" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.4 }}
              >
                {isIndustrialHome ? (
                  "Made to clean the toughest industrial messes. Safe enough to use every day. Get the clean you can trust."
                ) : isHousehold ? (
                  "Tough enough for industrial messes, safe enough for your home. Get the clean you can trust."
                ) : (
                  "Tough enough for industrial messes, safe enough for your home. Get the clean you can trust."
                )}
              </motion.p>
            </motion.div>

            {/* Call to Action */}
            <motion.div
              className="w-full flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.7 }}
            >
              <Button
                variant="primary"
                size="large"
                onClick={handleButtonClick}
              >
                {isIndustrialHome ? "Contact Us Now" : "Start Cleaning"}
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Mascot positioned absolutely within the hero section */}
      <Mascot />

      {/* Bottom fade effect */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-16"
        style={{
          background: 'linear-gradient(to top, rgba(255, 255, 255, 0.8), transparent)'
        }}
      />

      {/* Product Divider positioned absolutely within the hero section */}
      <ProductDivider flip={false} />

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </motion.section>
  );
};

export default Hero;